import { ERROR_CODES, type ApiErrorBody, type ErrorCode } from "@gatiq/shared";

/**
 * Thin fetch wrapper. Auth rides in httpOnly cookies, so every call sends
 * credentials and no token is ever handled by JavaScript.
 */
/** "branding.supportEmail" → "Support email", "pointsPerCode" → "Points per code". */
function fieldLabel(key: string): string {
  if (key.includes(":")) return "Role name";
  const last = key.split(".").pop() ?? key;
  const words = last.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The server's generic "check the highlighted fields" is only useful where a
 * form highlights them; everywhere else it names nothing. Say which field and why.
 */
function messageOf(body: ApiErrorBody): string {
  const first = body.error.fields ? Object.entries(body.error.fields)[0] : undefined;
  if (body.error.code === ERROR_CODES.VALIDATION_FAILED && first && /highlighted fields/i.test(body.error.message)) {
    return `${fieldLabel(first[0])}: ${first[1]}`;
  }
  return body.error.message;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly fields: Record<string, string> | undefined;
  /**
   * Anything else the endpoint attached to the error — for example the
   * contrast validator's `issues`, each with the measured ratio and a
   * suggested passing colour. Kept so the server stays the authority on
   * whether a theme is readable; the client's live check is a convenience,
   * not a second source of truth.
   */
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(messageOf(body));
    this.name = "ApiClientError";
    this.status = status;
    this.code = body.error.code;
    this.fields = body.error.fields;

    const { code: _code, message: _message, fields: _fields, ...rest } = body.error as Record<
      string,
      unknown
    > & ApiErrorBody["error"];
    this.details = rest;
  }
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/* ── session expiry ──────────────────────────────────────────────────
 *
 * The access cookie lasts fifteen minutes; the refresh cookie lasts a week.
 * Without the exchange between them, a session simply stops working partway
 * through the day and every screen reports it in its own words — "Could not
 * load branding", an empty table, a silent failure — while the app still
 * believes someone is signed in.
 *
 * So a 401 is handled here rather than by each caller. One attempt to refresh,
 * then either the original request is replayed and nobody notices, or the
 * session is genuinely over and the app is told to sign the user out.
 */
let onSessionEnded: (() => void) | null = null;

/** `AuthProvider` registers the app's "forget the user" action here. */
export function setSessionEndedHandler(fn: (() => void) | null): void {
  onSessionEnded = fn;
}

/**
 * True once a session has ended on its own rather than by the user asking.
 * The login screens read it to explain the redirect instead of appearing for
 * no reason in the middle of someone's work.
 *
 * Only `AuthProvider` sets it, and only when there was a signed-in user to
 * lose. A 401 on a first visit is not an expiry — telling someone opening a
 * printed link for the first time that their session ran out is a lie, and a
 * confusing one, since they have never had a session at all.
 */
let expired = false;
export function markSessionExpired(): void {
  expired = true;
}
export function sessionExpired(): boolean {
  return expired;
}
export function clearSessionExpired(): void {
  expired = false;
}

/**
 * Endpoints where a 401 is the answer, not a stale token: signing in with the
 * wrong password, verifying a wrong OTP, or the refresh call itself — which
 * would otherwise recurse. `/auth/me` is deliberately NOT here, so a page
 * opened after the access cookie lapsed restores the session instead of
 * bouncing someone to the login screen they could have skipped.
 */
function isCredentialExchange(path: string): boolean {
  return (
    path.endsWith("/login") ||
    path.endsWith("/refresh") ||
    path.endsWith("/logout") ||
    path.endsWith("/set-password") ||
    path.includes("/otp/")
  );
}

/*
 * One refresh, shared. A screen typically fires several requests at once — the
 * karigar home asks for the profile and the ledger together — and they would
 * all 401 within milliseconds of each other. Without this they would each
 * rotate the refresh token, and every rotation after the first would be
 * replaying a token the server has already retired.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch("/api/auth/refresh", { method: "POST", credentials: "include" })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function request<T>(method: Method, path: string, body?: unknown, retry = true): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && !isCredentialExchange(path)) {
    if (await refreshSession()) {
      return request<T>(method, path, body, false);
    }
    // Nothing left to sign in with. Reported once, here, rather than surfacing
    // as a different broken screen every time. Whether this counts as an
    // *expiry* is `AuthProvider`'s call — it is the only thing that knows
    // whether there was a user to lose.
    onSessionEnded?.();
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  if (!res.ok) {
    throw new ApiClientError(res.status, toErrorBody(res.status, text));
  }
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Not every failure is our own `{ error }` JSON. nginx answers a 502 or 413
 * with an HTML page, and `/health/ready` answers 503 with its service report.
 * Both used to crash here (SyntaxError, or a TypeError on `body.error`)
 * instead of becoming an ApiClientError. A JSON body without `error` is kept
 * whole in `details`, so a caller can still read what the server said.
 */
function toErrorBody(status: number, text: string): ApiErrorBody {
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Not JSON: an HTML error page, or empty.
  }
  const obj = parsed !== null && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  if (obj.error !== null && typeof obj.error === "object") return obj as unknown as ApiErrorBody;
  return {
    error: { ...obj, code: ERROR_CODES.INTERNAL, message: `The server answered with an error (${status}).` },
  } as ApiErrorBody;
}

/**
 * Downloads, images and new tabs go straight to the server, past request()'s
 * 401 → refresh → retry. Calling this first renews an idle session, so a file
 * fetched after fifteen quiet minutes still arrives instead of a raw 401.
 */
export async function freshSession(): Promise<void> {
  await request("GET", "/auth/me").catch(() => undefined);
}

/**
 * Opens a same-origin file in a new tab once the session is fresh. The tab is
 * opened synchronously (inside the click) so pop-up blockers allow it, then
 * pointed at the file.
 */
export function openFresh(url: string): void {
  const tab = window.open("about:blank", "_blank");
  void freshSession().then(() => {
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  });
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
